'use client';

import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { openErrorModal } from '@/lib/redux/slices/errorModalSlice';
import { selectUsageData } from '@/lib/redux/slices/usageSlice';
import Constants from '@/utils/constants';
import { Crown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useState } from 'react';

interface CrownButtonProps {
  text?: string;
  isMobileView?: boolean;
}

// Crown silhouette mask: solid-white SVG used to clip the shimmer to the crown shape only
const CROWN_MASK = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg width="21" height="20" viewBox="0 0 21 20" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M4.90687 15.3504C3.97543 15.1668 3.54049 14.5863 3.32231 13.7991C2.49222 10.804 1.6637 7.80846 0.83378 4.81332C0.635262 4.09687 0.725969 3.47709 1.43086 3.07209C2.08962 2.69359 2.66242 2.89503 3.19496 3.34779C4.40205 4.37404 5.61086 5.39828 6.92072 6.50968C7.24299 5.92997 7.52301 5.44224 7.78791 4.94652C8.39178 3.81648 8.9315 2.64677 9.61726 1.56878C9.87065 1.17048 10.4617 0.762151 10.8969 0.764168C11.3282 0.766167 11.9224 1.18325 12.158 1.58506C13.0796 3.15707 13.8876 4.79509 14.8037 6.53348C15.9906 5.52244 17.1021 4.54792 18.2482 3.61541C18.6144 3.31747 19.0491 2.99462 19.4924 2.92066C20.5365 2.74645 21.2116 3.64198 20.9097 4.78709C20.2731 7.20187 19.5891 9.60429 18.9232 12.0114C18.7692 12.5681 18.6086 13.123 18.4532 13.6793C18.1045 14.9283 17.5721 15.3642 16.3043 15.3659C12.5316 15.3708 8.75891 15.3614 4.90687 15.3504Z" fill="white"/>' +
    '<path d="M12.7105 19.0337C10.0451 19.0337 7.47176 19.027 4.89849 19.0381C4.2007 19.0412 3.51936 18.9724 3.52696 18.0979C3.53398 17.2898 4.17218 17.1935 4.8447 17.195C8.85783 17.2045 12.871 17.2045 16.8841 17.1951C17.5541 17.1935 18.1967 17.2817 18.2098 18.0901C18.224 18.9637 17.5472 19.0459 16.8464 19.0381C15.4986 19.023 14.1506 19.0337 12.7105 19.0337Z" fill="white"/>' +
    '</svg>'
)}")`;

const CrownSVG = ({ slowShimmer }: { slowShimmer?: boolean }) => (
  <div className='h-[17px] w-[17px] transition-all duration-300 hover:scale-110 hover:brightness-95 relative overflow-hidden group cursor-pointer'>
    {/* SVG Container */}
    <div
      className='relative z-10 [&>svg]:block [&>svg]:h-full [&>svg]:w-full [&>svg]:overflow-visible'
      dangerouslySetInnerHTML={{ __html: Constants.crown }}
    />
    {/* Shimmer overlay — masked to crown silhouette so it doesn't bleed into surrounding space */}
    <div
      className='absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20'
      style={{
        WebkitMaskImage: CROWN_MASK,
        maskImage: CROWN_MASK,
        WebkitMaskSize: 'contain',
        maskSize: 'contain' as string,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
      }}
    >
      <div
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
          animation: slowShimmer
            ? 'crownShimmerSlow 4s infinite linear'
            : 'crownShimmer 1.5s infinite linear',
          width: '200%',
          height: '100%',
          position: 'absolute',
          left: '-100%',
        }}
      />
    </div>
    <style jsx global>{`
      @keyframes crownShimmer {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(100%);
        }
      }
      @keyframes crownShimmerSlow {
        0% {
          transform: translateX(0%);
        }
        50% {
          transform: translateX(100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
      @keyframes textShimmer {
        /* Pause 0–50% (5s of a 10s animation): hidden, parked off-right */
        0%,
        50% {
          background-position: 100% 0;
          opacity: 0;
        }

        /* Turn on right before moving */
        52% {
          opacity: 1;
        }

        /* Fade out while still off-left */
        100% {
          background-position: -100% 0;
          opacity: 0;
        }
      }
    `}</style>
  </div>
);

const CrownButton: React.FC<CrownButtonProps> = ({ text, isMobileView }) => {
  const dispatch = useAppDispatch();
  const usageData = useAppSelector(selectUsageData);
  const tMarketing = useTranslations('marketing');
  const [isMounted, setIsMounted] = useState(false);

  // Determine paid status from zadanie data in Redux (no API call needed).
  // Gates on the typed `is_paid_plan` flag, never the display name — see
  // ZadaniePlanInfo.is_paid_plan. Must agree with SidebarRail's own gate.
  const zadanie = usageData?.zadanie ?? null;
  const isPaid = zadanie?.plan.is_paid_plan === true;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) {
    return (
      <div className='flex items-center justify-center gap-2 min-h-[44px] min-w-[44px]'>
        <div className='h-5 w-5 bg-surface-muted animate-pulse rounded' />
        {text && <span className='text-sm'>{text}</span>}
      </div>
    );
  }

  // Hide crown button for paid users
  if (isPaid) {
    return null;
  }

  return (
    // A real <button> (was a non-focusable <div onClick>): keyboard-operable and
    // carries an accessible name. When `text` is shown it IS the visible label
    // (so the name matches what's on screen); icon-only callers fall back to the
    // marketing CTA string. (#5383)
    <button
      type='button'
      aria-label={text ?? tMarketing('upgradeToPremium')}
      className='flex items-center justify-center gap-2 cursor-pointer min-h-[44px] min-w-[44px] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary/60'
      onClick={() => {
        dispatch(
          openErrorModal({
            errorCode: 402,
            source: 'search',
            isManualOpen: true, // User clicked crown button, not an actual error
          })
        );
      }}
    >
      {isMobileView ? (
        <Crown size={18} color='gold' fill='gold' />
      ) : (
        <CrownSVG slowShimmer={!text} />
      )}
      {text && (
        <span className='text-sm text-brand-tertiary-color dark:text-brand-tertiary relative inline-block overflow-hidden'>
          {text}
          {/* Shimmer overlay using background-clip: text to clip shimmer to text shape only */}
          <span
            className='absolute inset-0 pointer-events-none'
            aria-hidden='true'
            data-text={text}
            style={{
              color: 'transparent',
              background:
                'linear-gradient(105deg, transparent 42%, var(--shimmer-highlight) 50%, transparent 58%)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              animation: 'textShimmer 10s infinite linear',
            }}
          >
            {text}
          </span>
        </span>
      )}
    </button>
  );
};

export default CrownButton;
